import { useState } from "react";
import { Card, CardContent, CardFooter, Spinner } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useCapabilities } from "@/hooks/useCapabilities";
import { useGlobalStore } from "@/stores/GlobalStore";
import { getDashboardConfig } from "@/config/dashboards";
import { brandIconSrc } from "@/config/tenants";

export default function MenuCard() {
  const navigate = useNavigate();
  const { canAuthorWorkflows, canViewKnowledgeHub, canViewMonitoring, isSettled } =
    useCapabilities();
  const { brand } = useBrand();
  const { sidebar } = useGlobalStore();
  const { isSidebarCompact } = sidebar;
  const { t } = useTranslation();

  // Tenant packs do not all ship every menu icon, so a tile whose image 404s
  // drops the image and renders label-only rather than showing the browser's
  // broken-image glyph. Same approach as Sidebar's `failedImages` set.
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const dashboard = getDashboardConfig(brand.folder);

  // Build list from the shared capability layer
  const list = [
    // Add dashboard as first item if configured to show in sidebar
    ...(dashboard?.showInSideBar && dashboard?.url
      ? [
          {
            name: dashboard.title ? t(dashboard.title) : t("Dashboard"),
            title: dashboard.subtitle ? t(dashboard.subtitle) : t("Dashboard"),
            img: brandIconSrc(brand.folder, "dashboard.svg"),
            link: `/${dashboard.urlSegment ?? "dashboard"}`,
            dataID: "dashboard",
          },
        ]
      : []),
    {
      name: t(brand.copy.menuItems?.Pathfinder || "Chat"),
      title: t("Chat"),
      img: brandIconSrc(brand.folder, "pathfinder.svg"),
      link: "/chat",
      dataID: "chat",
    },
    {
      name: t(brand.copy.menuItems?.["Library"] || "Library"),
      title: t("library.menuSubtitle"),
      img: brandIconSrc(brand.folder, "library.svg"),
      link: "/library",
      dataID: "library",
    },
    // Workflow Builder - same `canAuthorWorkflows` capability and same position
    // relative to Library as the sidebar entry in `SidebarMenuItems.tsx`, so the
    // landing tiles and the sidebar cannot disagree about what a user can reach.
    // Ships for every tenant with no `isFolder` gating, per ADR-0021.
    ...(canAuthorWorkflows
      ? [
          {
            name: t("workflowBuilder.nav.title"),
            title: t("workflowBuilder.menuSubtitle"),
            img: brandIconSrc(brand.folder, "workflow-builder.svg"),
            link: "/workflow-builder",
            dataID: "workflow-builder",
          },
          // Automated Workflows (LSEG-107) - same capability and same position
          // relative to the Builder as the sidebar entry, so the tiles and the
          // sidebar cannot disagree about what a user can reach. Scheduling a
          // workflow and triggering a run are mutations, so the read flag alone
          // is not enough.
          {
            name: t("automations.nav.title"),
            title: t("automations.menuSubtitle"),
            img: brandIconSrc(brand.folder, "workflow-automation.svg"),
            link: "/automations",
            dataID: "automations",
          },
        ]
      : []),
    // Knowledge Hub - check secure permission
    ...(canViewKnowledgeHub
      ? [
          {
            name: t(brand.copy.menuItems?.["Knowledge Hub"] || "Knowledge Hub"),
            title: t("Secure access to your data & knowledge"),
            img: brandIconSrc(brand.folder, "knowledge.svg"),
            link: "/datahub",
            dataID: "data-hub",
          },
        ]
      : []),
    // Monitoring - check secure permission (uses solutions.svg icon)
    ...(canViewMonitoring
      ? [
          {
            name: t(brand.copy.menuItems?.["Monitoring"] ?? "Monitoring"),
            title: t("Monitor AI usage and performance"),
            img: brandIconSrc(brand.folder, "solutions.svg"),
            link: "/metrics",
            dataID: "monitoring",
          },
        ]
      : []),
    // Models Overview - always available (no permission required)
    {
      name: t("Models Overview"),
      title: t("View available AI models and capabilities"),
      img: brandIconSrc(brand.folder, "models.svg"),
      link: "/models",
      dataID: "models-overview",
    },
    // Settings - available to all authenticated users
    {
      name: t(brand.copy.menuItems?.["Settings"] ?? "Settings"),
      title: t("Configure your AI platform"),
      img: brandIconSrc(brand.folder, "settings.svg"),
      link: "/settings",
      dataID: "settings",
    },
  ];

  // Show loading state until the RBAC grants have settled, so the Workflow
  // Builder tile is not briefly absent for a user who is entitled to it. This
  // now paints on most visits rather than being effectively dead code, so it
  // follows the Library page's pending convention — a spinner beside the label,
  // announced politely — instead of a bare line of text.
  if (!isSettled) {
    return (
      <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-3/5">
        <div aria-live="polite" className="flex items-center gap-3 text-default-500" role="status">
          <Spinner size="sm" />
          {t("Loading permissions...")}
        </div>
      </div>
    );
  }

  return (
    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 w-3/5">
      {list.map((item) => (
        <Card
          // `dataID` is the stable identifier for a tile. `title` is a translated
          // subtitle, so keying on it would remount cards on a language change and
          // silently drop one if two locales happen to translate to the same string.
          key={item.dataID}
          className="py-4 cursor-pointer transition-[transform,box-shadow] duration-200 hover:shadow-lg hover:scale-102 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          data-id={item.dataID}
          role="button"
          tabIndex={0}
          onClick={() => navigate(item.link)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(item.link);
            }
          }}
        >
          <CardContent className="flex justify-center items-center overflow-hidden">
            <div className="w-full flex flex-col items-center justify-center gap-2 py-4">
              {item.img && !failedImages.has(item.dataID) && (
                <img
                  alt=""
                  src={item.img}
                  style={{
                    borderRadius: 12,
                    height: brand.layout.menuItemsIconSize || 64,
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                  onError={() =>
                    setFailedImages((previous) => {
                      const next = new Set(previous);

                      next.add(item.dataID);

                      return next;
                    })
                  }
                />
              )}
              <div
                className={`text-center text-foreground font-bold ${!isSidebarCompact ? "text-sm" : "text-xl"}`}
              >
                {item.name}
              </div>
            </div>
          </CardContent>
          {isSidebarCompact && (
            <CardFooter className="px-4 pb-4 pt-0 justify-center">
              <small className="text-default-600 text-center">{item.title}</small>
            </CardFooter>
          )}
        </Card>
      ))}
    </div>
  );
}
